<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'

import { Icon } from '@iconify/vue'
import { onClickOutside, onKeyStroke, useDebounceFn } from '@vueuse/core'

import { sortCompanyListForDisplay } from '@/components/frontend/company-organization/companyListOrder'

import { useKeyboardNavigation } from '@/composables/useKeyboardNavigation'

import * as aggr from '@/api-generator/aggr'

import type { InputInstance } from 'element-plus'

interface Props {
  /** 公司與組織列表資料 */
  companyAndOrganizationList?: aggr.FECompanyGetCompaniesAndOrganizationsRs
  /** 當前選擇的公司 UUID */
  selectedCompanyUuid: string
  /** 當前選擇的組織 UUID */
  selectedOrganizationUuid: string
  /** 是否正在載入搜尋結果 */
  isLoading?: boolean
  /** 使用者所屬的公司總數（不受搜尋結果影響） */
  totalCompanyCount?: number
  /** 是否為 system staff */
  isSystemStaff?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  /** 選擇公司事件 */
  selectCompany: [companyUuid: string]
  /** 選擇組織事件 */
  selectOrganization: [organizationUuid: string]
  /** 搜尋公司及組織事件 */
  search: []
  /** 清空搜尋資料事件 - 立即觸發，不經過 debounce */
  clearSearchData: []
}>()

const debouncedSearch = useDebounceFn(() => {
  emit('search')
}, 300)

/** 搜尋結果中點選公司後，等待資料回來要自動選擇的公司 UUID */
const pendingCompanyUuid = ref<string | null>(null)

/** 處理輸入事件 */
const handleSearchInput = (value: string) => {
  pendingCompanyUuid.value = null
  isInternalSearching.value = true
  if (value && value.length > 0) {
    emit('clearSearchData')
    debouncedSearch()
  } else {
    emit('search')
  }
}

/** 合併的搜尋載入狀態 */
const isSearchingNow = computed(() => {
  return isInternalSearching.value || props.isLoading || false
})

/** 重組後的公司資料類型 */
interface CompanyWithOrganizations {
  companyUuid: string
  companyName: string
  organizations: aggr.EFCompanyOrganizationBaseRs[]
}

const term = defineModel<string>('term', {
  default: ''
})

const inputRef = ref<InputInstance | null>(null)
const panelRef = ref<HTMLDivElement | null>(null)
const isPanelVisible = ref(false)
const selectedCompanyUuidForDisplay = ref<string>('')

/** 內部搜尋載入狀態 - 用於在輸入時立即顯示 loading */
const isInternalSearching = ref(false)

const companiesMap = computed<Map<string, CompanyWithOrganizations>>(() => {
  const map = new Map<string, CompanyWithOrganizations>()

  if (!props.companyAndOrganizationList?.companyList) {
    return map
  }

  props.companyAndOrganizationList.companyList.forEach((company) => {
    const companyUuid = company.companyUuid || ''
    const organizations =
      props.companyAndOrganizationList?.organizationList?.filter((org) => org.companyUuid === companyUuid) || []

    map.set(companyUuid, {
      companyUuid,
      companyName: company.companyName || '',
      organizations
    })
  })

  return map
})

/** 公司列表（system staff 需將指定公司置頂，若當前選擇公司不同則排第二） */
const companyList = computed<CompanyWithOrganizations[]>(() => {
  return sortCompanyListForDisplay(Array.from(companiesMap.value.values()), {
    selectedCompanyUuid: props.selectedCompanyUuid,
    isSystemStaff: props.isSystemStaff
  })
})

/** 是否只有單一公司（優先使用外部傳入的總數，避免搜尋結果影響判斷） */
const isSingleCompany = computed(() => {
  if (props.totalCompanyCount != null) {
    return props.totalCompanyCount === 1
  }
  return companyList.value.length === 1
})

/** 搜尋輸入框 placeholder */
const searchPlaceholder = computed(() => (isSingleCompany.value ? '搜尋組織' : '搜尋公司,組織'))

/** 當前選擇的公司資料（從 Map 中取得） */
const selectedCompany = computed(() => {
  return companiesMap.value.get(props.selectedCompanyUuid)
})

/** 當前選擇的公司名稱 */
const selectedCompanyName = computed(() => {
  return selectedCompany.value?.companyName || ''
})

/** 當前選擇的公司的組織列表 */
const selectedCompanyOrganizations = computed(() => {
  return selectedCompany.value?.organizations || []
})

/** 當前選擇的組織名稱 */
const selectedOrganizationName = computed(() => {
  const organization = selectedCompanyOrganizations.value.find(
    (org) => org.companyOrganizationUuid === props.selectedOrganizationUuid
  )
  return organization?.companyOrganizationName || ''
})

/** 當前要顯示組織列表的公司（從 Map 中取得，當前選擇的組織置頂） */
const displayCompanyOrganizations = computed(() => {
  if (!selectedCompanyUuidForDisplay.value) {
    return []
  }
  const orgs = [...(companiesMap.value.get(selectedCompanyUuidForDisplay.value)?.organizations || [])]

  // 只有在顯示當前選擇的公司時，才將選中的組織置頂
  if (selectedCompanyUuidForDisplay.value === props.selectedCompanyUuid) {
    const selectedIndex = orgs.findIndex((o) => o.companyOrganizationUuid === props.selectedOrganizationUuid)
    if (selectedIndex > 0) {
      const [selected] = orgs.splice(selectedIndex, 1)
      orgs.unshift(selected)
    }
  }

  return orgs
})

// ================== 搜尋關鍵字時顯示的狀態 ==================

/** 公司列表 */
const searchedCompanyList = computed(() => props.companyAndOrganizationList?.companyList || [])
/** 組織列表 */
const searchedOrganizationList = computed(() => props.companyAndOrganizationList?.organizationList || [])
/** 公司總數 */
const searchedCompanyCount = computed(() => {
  return props.companyAndOrganizationList?.companyCount || 0
})
/** 組織總數 */
const searchedOrganizationCount = computed(() => {
  return props.companyAndOrganizationList?.organizationCount || 0
})

/** 搜尋結果中可見的公司數量 */
const visibleCompanyCount = computed(() => {
  return isSingleCompany.value ? 0 : searchedCompanyList.value.length
})

/** 搜尋結果總項目數（公司 + 組織） */
const totalSearchResultsCount = computed(() => {
  return visibleCompanyCount.value + searchedOrganizationList.value.length
})

/** 關閉面板 */
const closePanel = async () => {
  isPanelVisible.value = false
  selectedCompanyUuidForDisplay.value = ''
  resetFocus()

  if (term.value && term.value.length > 0) {
    term.value = ''
    // NOTE: 等待 v-model 更新完成後再觸發搜尋，確保父組件接收到最新的搜尋關鍵字
    await nextTick()
    emit('search')
  }
}

/** 切換面板顯示狀態 */
const togglePanel = async () => {
  // 開啟面板前，先設置要顯示的公司 UUID（避免閃爍）
  if (!isPanelVisible.value) {
    if (isSingleCompany.value) {
      selectedCompanyUuidForDisplay.value = companyList.value[0]?.companyUuid
    } else if (props.selectedCompanyUuid) {
      selectedCompanyUuidForDisplay.value = props.selectedCompanyUuid
    }
  }

  isPanelVisible.value = !isPanelVisible.value
  await nextTick()
  inputRef.value?.focus()

  // 關閉面板時，重置顯示狀態
  if (!isPanelVisible.value) {
    selectedCompanyUuidForDisplay.value = ''
  }
}

/** 選擇公司並顯示其組織列表
 * @param companyUuid 公司 UUID
 */
const handleCompanyClick = (companyUuid: string) => {
  selectedCompanyUuidForDisplay.value = companyUuid
}

/** 選擇組織並關閉面板
 * @param companyUuid 公司 UUID
 * @param organizationUuid 組織 UUID
 */
const handleOrganizationClick = (companyUuid: string, organizationUuid: string) => {
  emit('selectCompany', companyUuid)
  emit('selectOrganization', organizationUuid)
  closePanel()
}

/** 選擇公司並自動進入其第一個組織
 * @param company 公司資料
 */
const handleSearchedCompanyClick = async (company: aggr.FECompanyBaseRs) => {
  const companyUuid = company.companyUuid || ''
  pendingCompanyUuid.value = companyUuid
  term.value = ''
  await nextTick()
  emit('search')
}

/** 選擇組織並設定所屬公司
 * @param organization 組織資料
 */
const handleSearchedOrganizationClick = (organization: aggr.EFCompanyOrganizationBaseRs) => {
  const companyUuid = organization.companyUuid || ''
  const organizationUuid = organization.companyOrganizationUuid || ''

  emit('selectCompany', companyUuid)
  emit('selectOrganization', organizationUuid)
  closePanel()
}

/** 判斷公司是否為當前顯示的公司
 * @param companyUuid 公司 UUID
 */
const isCompanyActive = (companyUuid: string): boolean => {
  return selectedCompanyUuidForDisplay.value === companyUuid
}

/** 判斷組織是否為選中狀態
 * @param organizationUuid 組織 UUID
 */
const isOrganizationSelected = (organizationUuid: string): boolean => {
  return (
    props.selectedOrganizationUuid === organizationUuid &&
    selectedCompanyUuidForDisplay.value === props.selectedCompanyUuid
  )
}

/** 處理搜尋結果中點選公司後，自動選擇第一個組織 */
const handlePendingCompanySelection = () => {
  if (!pendingCompanyUuid.value) {
    return
  }

  const companyUuid = pendingCompanyUuid.value
  pendingCompanyUuid.value = null
  const firstOrg = companiesMap.value.get(companyUuid)?.organizations?.[0]

  if (firstOrg) {
    emit('selectCompany', companyUuid)
    emit('selectOrganization', firstOrg.companyOrganizationUuid || '')
    closePanel()
  } else {
    ElMessage.error('該公司沒有組織，無法選擇')
  }
}

watch(
  () => props.isLoading,
  (newValue, oldValue) => {
    if (oldValue === true && newValue === false) {
      isInternalSearching.value = false
      handlePendingCompanySelection()
    }
  }
)

onClickOutside(panelRef, closePanel)
onKeyStroke('Escape', (e) => {
  if (isPanelVisible.value) {
    e.preventDefault()
    closePanel()
  }
})

// ================== 搜尋結果鍵盤導航 ==================

/** 根據索引獲取對應的項目類型和資料 */
const getItemByIndex = (index: number): { type: 'company' | 'organization'; item: any } | null => {
  if (index < 0 || index >= totalSearchResultsCount.value) {
    return null
  }

  if (index < visibleCompanyCount.value) {
    return { type: 'company', item: searchedCompanyList.value[index] }
  }

  const orgIndex = index - visibleCompanyCount.value
  return { type: 'organization', item: searchedOrganizationList.value[orgIndex] }
}

/** 鍵盤導航啟用條件 */
const isKeyboardNavEnabled = computed(() => {
  return isPanelVisible.value && term.value.length > 0 && !isSearchingNow.value
})

// 鍵盤導航
const { isFocused, resetFocus, setFocusedIndex } = useKeyboardNavigation({
  enabled: isKeyboardNavEnabled,
  itemCount: totalSearchResultsCount,
  containerRef: panelRef,
  focusedSelector: '.searched-list-item.focused',
  inputRef: inputRef,
  onSelect: (index) => {
    const result = getItemByIndex(index)
    if (result?.type === 'company') {
      handleSearchedCompanyClick(result.item)
    } else if (result?.type === 'organization') {
      handleSearchedOrganizationClick(result.item)
    }
  },
  resetTrigger: term
})

/** 判斷公司項目是否被焦點選中 */
const isCompanyFocused = (companyIndex: number): boolean => {
  return isFocused(companyIndex)
}

/** 判斷組織項目是否被焦點選中 */
const isOrganizationFocused = (orgIndex: number): boolean => {
  return isFocused(visibleCompanyCount.value + orgIndex)
}
</script>

<template>
  <div ref="panelRef" class="relative min-w-0">
    <div
      class="flex h-12 cursor-pointer flex-col justify-center gap-1 rounded-lg px-2 py-1.5 transition-colors hover:bg-[#E3E6ED]"
      @click="togglePanel"
    >
      <span class="flex-shrink-0 truncate text-xs font-medium leading-[18px] text-app-black-600">
        {{ selectedCompanyName }}
      </span>
      <div class="flex items-center gap-1 rounded-lg">
        <span class="truncate text-base font-semibold leading-6 text-app-black-900 md:text-xl">
          {{ selectedOrganizationName }}
        </span>
        <span class="flex h-6 w-6 flex-shrink-0 items-center justify-center">
          <Icon icon="ri:arrow-down-s-fill" width="20px" height="20px" />
        </span>
      </div>
    </div>
    <Transition name="fade">
      <div
        v-if="isPanelVisible"
        class="list-container absolute left-0 top-0 z-[2001] w-[620px] max-w-[calc(100vw-32px)] rounded-xl px-4 pb-4 pt-2"
      >
        <div class="mb-2">
          <el-input
            ref="inputRef"
            v-model="term"
            class="company-search-input"
            :placeholder="searchPlaceholder"
            clearable
            @input="handleSearchInput"
          />
        </div>
        <template v-if="term.length === 0 && !isInternalSearching">
          <div class="grid" :class="isSingleCompany ? 'grid-cols-1' : 'grid-cols-5'">
            <div v-if="!isSingleCompany" class="col-span-2">
              <div class="mb-2">
                <div class="px-4 text-xs text-app-black-200">公司列表</div>
              </div>
              <el-scrollbar max-height="176px" class="custom-scrollbar pr-4">
                <ul>
                  <li v-for="company in companyList" :key="company.companyUuid">
                    <button
                      type="button"
                      class="list-button w-full text-left"
                      :class="{ 'list-button-active': isCompanyActive(company.companyUuid) }"
                      @click="handleCompanyClick(company.companyUuid)"
                    >
                      <span class="truncate">{{ company.companyName }}</span>
                    </button>
                  </li>
                </ul>
              </el-scrollbar>
            </div>
            <div :class="isSingleCompany ? 'col-span-1' : 'col-span-3'">
              <div v-if="!isSingleCompany" class="mb-2">
                <div class="px-4 text-xs text-app-black-200">旗下組織</div>
              </div>
              <Transition name="fade" mode="out-in">
                <el-scrollbar v-if="displayCompanyOrganizations.length > 0" max-height="176px" class="custom-scrollbar">
                  <ul>
                    <li v-for="org in displayCompanyOrganizations" :key="org.companyOrganizationUuid">
                      <button
                        type="button"
                        class="list-button w-full text-left"
                        :class="{ 'list-button-active': isOrganizationSelected(org.companyOrganizationUuid || '') }"
                        @click="
                          handleOrganizationClick(selectedCompanyUuidForDisplay, org.companyOrganizationUuid || '')
                        "
                      >
                        {{ org.companyOrganizationName }}
                      </button>
                    </li>
                  </ul>
                </el-scrollbar>
                <div v-else class="flex h-full items-center justify-center pb-12 text-sm text-app-black-400">
                  尚未建立組織
                </div>
              </Transition>
            </div>
          </div>
        </template>
        <template v-else>
          <div v-if="isSearchingNow" class="flex items-center justify-center pb-5">
            <div class="text-sm text-white">搜尋中...</div>
          </div>
          <div v-else>
            <template v-if="!isSingleCompany">
              <div>
                <div class="searched-list-title">公司 {{ searchedCompanyCount }} 筆</div>
                <el-scrollbar max-height="74px" class="custom-scrollbar-search">
                  <ul>
                    <li
                      v-for="(company, index) in searchedCompanyList"
                      :key="company.companyUuid"
                      class="searched-list-item"
                      :class="{ focused: isCompanyFocused(index) }"
                      @click="handleSearchedCompanyClick(company)"
                      @mouseenter="setFocusedIndex(index)"
                    >
                      <span class="searched-list-item-text">{{ company.companyName }}</span>
                    </li>
                  </ul>
                </el-scrollbar>
              </div>
              <div class="my-2 h-px w-full bg-app-black-700"></div>
            </template>
            <div>
              <div class="searched-list-title">組織 {{ searchedOrganizationCount }} 筆</div>
              <el-scrollbar max-height="74px" class="custom-scrollbar-search">
                <ul>
                  <li
                    v-for="(organization, index) in searchedOrganizationList"
                    :key="organization.companyOrganizationUuid"
                    class="searched-list-item"
                    :class="{ focused: isOrganizationFocused(index) }"
                    @click="handleSearchedOrganizationClick(organization)"
                    @mouseenter="setFocusedIndex(visibleCompanyCount + index)"
                  >
                    <span class="searched-list-item-text">{{ organization.companyOrganizationName }}</span>
                  </li>
                </ul>
              </el-scrollbar>
            </div>
          </div>
        </template>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.list-container {
  border-radius: 12px;
  background: rgba(32, 33, 36, 0.85);
  box-shadow:
    0 18.1px 36.2px -9.05px rgba(16, 28, 40, 0.1),
    0 9.05px 18.1px -9.05px rgba(16, 28, 40, 0.06);
  -webkit-backdrop-filter: blur(16px) !important;
  backdrop-filter: blur(16px);
}

/* 搜尋輸入框樣式 */
.company-search-input :deep(.el-input__wrapper) {
  background-color: transparent;
  border: none;
  outline: none;
  box-shadow: none;
  height: 20px;
  margin: 16px 12px;
}

.company-search-input :deep(.el-input__wrapper:hover),
.company-search-input :deep(.el-input__wrapper.is-focus) {
  box-shadow: none;
}

.company-search-input :deep(.el-input__inner) {
  color: #ffffff;
  font-size: 14px;
}

.company-search-input :deep(.el-input__inner::placeholder) {
  color: #90969f;
}

/* el-scrollbar 自定義樣式 - 主列表 */
.custom-scrollbar :deep(.el-scrollbar__bar.is-vertical) {
  width: 6px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.custom-scrollbar:hover :deep(.el-scrollbar__bar.is-vertical) {
  opacity: 1;
}

.custom-scrollbar :deep(.el-scrollbar__thumb) {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.custom-scrollbar :deep(.el-scrollbar__thumb):hover {
  background-color: rgba(255, 255, 255, 0.5);
}

/* el-scrollbar 自定義樣式 - 搜尋結果列表 */
.custom-scrollbar-search :deep(.el-scrollbar__bar.is-vertical) {
  width: 6px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.custom-scrollbar-search:hover :deep(.el-scrollbar__bar.is-vertical) {
  opacity: 1;
}

.custom-scrollbar-search :deep(.el-scrollbar__thumb) {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.custom-scrollbar-search :deep(.el-scrollbar__thumb):hover {
  background-color: rgba(255, 255, 255, 0.5);
}

.list-button {
  @apply flex h-9 min-w-0 items-center overflow-hidden rounded-lg px-4 py-2 text-sm text-white transition-colors hover:bg-[rgba(255,255,255,0.1)];
}

.list-button-active {
  @apply bg-[rgba(255,255,255,0.08)];
}

.searched-list-title {
  @apply mb-2 px-4 text-xs text-app-black-200;
}

.searched-list-item {
  @apply cursor-pointer rounded-lg px-4 py-2 hover:bg-[rgba(255,255,255,0.1)];

  &.focused {
    background-color: rgba(255, 255, 255, 0.1);
  }
}

.searched-list-item-text {
  @apply text-sm leading-5 text-white;
}

/* 淡入淡出動畫 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
